import React from "react";
import dayjs from "dayjs";
import { CalendarTodayOutlined as Calendar, PeopleAltOutlined as Users, BarChart, ShowChart, RequestQuote, EventAvailable, TodayOutlined, AccessTimeOutlined } from "@mui/icons-material";
import RetainersTable from "./RunnitRetainers";
import RunnitProjectProfitability, { isCostKnown } from "./RunnitProjectProfitability";
import RunnitResourceUtilization from "./RunnitResourceUtilization";
import RunnitArAging from "./RunnitArAging";
import RunnitProfitabilityTrend from "./RunnitProfitabilityTrend";
import RunnitDailyHours from "./RunnitDailyHours";
import RunnitHoursPosted, { HOURS_POSTED_TYPES, monthLabel } from "./RunnitHoursPosted";
import MeetingTimeReport from "../pages/MeetingTimeReport";
import {
    getProjectProfitability,
    getResourceUtilization,
    getRetainers,
    getArAging,
    getProfitabilityTrend,
    getDailyHours,
    getHoursPosted,
} from "../redux/ducks/reports";
import { toLocalDay } from "../utils/dateUtils";

// Project status enum from project.model.js (kept in sync with backend).
const PROJECT_STATUSES = ["All", "Awaiting Authorization", "Pending", "In Progress", "Testing", "Ready for Review", "Approved", "Completed", "On Hold", "Cancelled"];
const RETAINER_TYPES = ["All", "Client", "Account", "Group"];

const money = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const moneyRounded = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const hours = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const monthSpan = (startDate, endDate) => {
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;
    if (!start || !start.isValid() || !end || !end.isValid()) return "";
    return start.isSame(end, "month") ? start.format("MMM YYYY") : `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}`;
};

/**
 * One descriptor per report on the Runnit Reports page. Adding a report means adding an entry
 * here plus its table component — the page itself reads everything below and needs no changes.
 *
 * key            route segment and tab value
 * permission     optional permission flag; the tab is hidden without it
 * standalone     report renders its own filters and summary, bypassing the shared chrome
 * filters        which shared filter controls to show ({ dates, dropdown }); `dates` is
 *                'month' (whole-month pickers), 'day' (calendar-day pickers) or false (no dates)
 * maxRangeDays   longest range the endpoint accepts; the pickers bound each other to it and a
 *                longer range inherited from another report blocks Generate until it is shortened
 * fetch          dispatches the report's action; receives { dispatch, startDate, endDate, onDone, setContext };
 *                onDone takes the rows and, optionally, the range the endpoint says they cover
 * filterRows     client-side narrowing applied after fetch
 * summaryTitles  card titles shown as placeholders before the first fetch
 * summary        builds the summary cards from the visible rows
 * csv            export filename, column definitions and an optional `rows` flattener
 * render         renders the report body
 */
export const RUNNIT_REPORTS = [
    {
        key: "retainer",
        label: "Retainer Report",
        icon: <Calendar fontSize="small" />,
        filters: { dates: 'month', dropdown: { name: "clientType", label: "Retainer Type", options: RETAINER_TYPES } },
        fetch: ({ dispatch, startDate, endDate, onDone }) => dispatch(getRetainers(startDate, endDate, onDone)),
        filterRows: (rows, { clientType }) => (clientType === "All" ? rows : rows.filter((r) => r.type === clientType)),
        summaryTitles: ["Total Hours Purchased", "Total Hours Used", "Average Margin"],
        summary: (rows) => {
            const purchased = rows.reduce((s, r) => s + (r.billingTerms?.hours || 0), 0);
            const used = rows.reduce((s, r) => s + (r.time / 60 || 0), 0);
            const avgMargin = rows.length
                ? (
                      rows.reduce((s, r) => {
                          const hp = r.billingTerms?.hours || 0;
                          const hu = (r.time / 60) || 0;
                          return s + (hp > 0 ? ((hp - hu) / hp) * 100 : 0);
                      }, 0) / rows.length
                  ).toFixed(2)
                : 0;
            return [
                { title: "Total Hours Purchased", value: hours(purchased), color: "primary" },
                {
                    title: "Total Hours Used",
                    value: hours(used),
                    color: "success.main",
                    subtext: purchased > 0 ? `${((used / purchased) * 100).toFixed(1)}% utilization` : "0% utilization",
                },
                { title: "Average Margin", value: `${avgMargin}%`, color: "secondary" },
            ];
        },
        csv: {
            file: ({ stamp }) => `retainers_${stamp}.csv`,
            cols: [
                { header: "Name", get: (r) => r.name },
                { header: "Type", get: (r) => r.type },
                { header: "Hours Committed", get: (r) => r.billingTerms?.hours || 0 },
                { header: "Hours Posted", get: (r) => ((r.time || 0) / 60).toFixed(2) },
                { header: "Cost", get: (r) => (r.cost || 0).toFixed(2) },
                { header: "Revenue", get: (r) => (r.billingTerms?.charge || 0).toFixed(2) },
            ],
        },
        render: ({ data, loading, hasFetched, handlers }) => (
            <RetainersTable data={data} loading={loading} hasFetched={hasFetched} onSave={handlers.onRetainerSave} />
        ),
    },

    {
        key: "profitability",
        label: "Fixed-Price Profitability",
        icon: <BarChart fontSize="small" />,
        filters: { dates: 'month', dropdown: { name: "projectStatus", label: "Project Status", options: PROJECT_STATUSES } },
        fetch: ({ dispatch, startDate, endDate, onDone }) =>
            dispatch(getProjectProfitability(startDate, endDate, (data) => onDone(data ? data.rows : null, data))),
        filterRows: (rows, { projectStatus }) => (projectStatus === "All" ? rows : rows.filter((r) => r.status === projectStatus)),
        banner: ({ rows, startDate, endDate }) => {
            if (!rows.length) return null;
            const span = monthSpan(startDate, endDate);
            const measurable = rows.filter(isCostKnown);
            const groupCount = rows.length - measurable.length;
            const understated = measurable.filter((r) => r.costUnderstated).length;
            const groups = groupCount
                ? ` ${groupCount} group row${groupCount === 1 ? " is" : "s are"} listed for reference but left out of the summary totals above: time is logged against a group's projects, never against the group, so a group's labor cost is not known here.`
                : "";
            const wage = understated
                ? ` ${understated} of the ${measurable.length} project rows below include hours logged by someone with no hourly wage on file, so their Labor Cost is lower than what the work really cost — those rows are marked with a warning icon.`
                : "";
            return `${span ? `Listing every fixed-price project and group open in ${span}. ` : ""}Actual Hours and Labor Cost are each project's lifetime totals — the period chooses which projects are listed, not which hours are counted.${groups}${wage}`;
        },
        summaryTitles: ["Total Quoted Revenue", "Total Labor Cost", "Total Profit"],
        summary: (rows) => {
            const measurable = rows.filter(isCostKnown);
            const excluded = rows.length - measurable.length;
            const excludedNote = excluded ? `Project rows only — ${excluded} group row${excluded === 1 ? "" : "s"} excluded` : undefined;
            const revenue = measurable.reduce((s, p) => s + (p.quotedPrice || 0), 0);
            const cost = measurable.reduce((s, p) => s + (p.actualCost || 0), 0);
            const profit = revenue - cost;
            return [
                { title: "Total Quoted Revenue", value: money(revenue), color: "primary", subtext: excludedNote },
                { title: "Total Labor Cost", value: money(cost), color: "warning.main", subtext: excludedNote },
                {
                    title: "Total Profit",
                    value: money(profit),
                    color: profit >= 0 ? "success.main" : "error.main",
                    subtext: revenue > 0 ? `${((profit / revenue) * 100).toFixed(1)}% margin` : "No quoted revenue to measure a margin against",
                },
            ];
        },
        csv: {
            file: ({ stamp }) => `fp_profitability_${stamp}.csv`,
            cols: [
                { header: "Name", get: (r) => r.name },
                { header: "Type", get: (r) => r.type || "Project" },
                { header: "Client", get: (r) => r.client },
                { header: "Status", get: (r) => r.status },
                { header: "Estimated Hours", get: (r) => r.estimatedTime || 0 },
                { header: "Quoted Hours", get: (r) => r.quotedTime || 0 },
                { header: "Actual Hours", get: (r) => (isCostKnown(r) ? ((r.time?.total?.["Fixed Price"] || 0) / 60).toFixed(2) : "N/A") },
                { header: "Quoted Price", get: (r) => (r.quotedPrice || 0).toFixed(2) },
                { header: "Labor Cost", get: (r) => (isCostKnown(r) ? (r.actualCost || 0).toFixed(2) : "N/A") },
                { header: "Labor Cost Understated", get: (r) => (isCostKnown(r) ? (r.costUnderstated ? "Yes" : "No") : "N/A") },
                { header: "Profit", get: (r) => (isCostKnown(r) ? ((r.quotedPrice || 0) - (r.actualCost || 0)).toFixed(2) : "N/A") },
                {
                    header: "Margin %",
                    get: (r) => (isCostKnown(r) && r.quotedPrice > 0 ? ((((r.quotedPrice || 0) - (r.actualCost || 0)) / r.quotedPrice) * 100).toFixed(1) : "N/A"),
                },
            ],
        },
        render: ({ data, loading, hasFetched }) => <RunnitProjectProfitability data={data} loading={loading} hasFetched={hasFetched} />,
    },

    {
        key: "resources",
        label: "Resource Utilization",
        icon: <Users fontSize="small" />,
        filters: { dates: 'month', dropdown: { name: "department", label: "Department", options: "departments" } },
        fetch: ({ dispatch, startDate, endDate, onDone, setContext }) => {
            // Auto-toggle monthly breakdown when the range exceeds ~1 month; a banner explains it.
            const groupByMonth = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) > 32;
            setContext({ groupedByMonth: groupByMonth });
            dispatch(getResourceUtilization(startDate, endDate, groupByMonth, onDone));
        },
        filterRows: (rows, { department }) => (department === "All" ? rows : rows.filter((r) => r.department === department)),
        banner: ({ context }) =>
            context.groupedByMonth ? "Showing monthly breakdown automatically because the selected range is longer than one month." : null,
        summaryTitles: ["Total Capacity", "Total Billable Hours", "Average Utilization"],
        summary: (rows) => {
            const capacity = rows.reduce((s, r) => s + (r.capacity || 0), 0);
            const billable = rows.reduce((s, r) => s + (r.billableHours || 0), 0);
            const avg = rows.length ? (rows.reduce((s, r) => s + (r.totalUtilization || 0), 0) / rows.length).toFixed(1) : 0;
            return [
                { title: "Total Capacity", value: hours(capacity), color: "primary" },
                { title: "Total Billable Hours", value: hours(billable), color: "success.main" },
                { title: "Average Utilization", value: `${avg}%`, color: "secondary" },
            ];
        },
        csv: {
            file: ({ stamp }) => `resource_utilization_${stamp}.csv`,
            cols: [
                { header: "Employee", get: (r) => r.employeeName },
                { header: "Department", get: (r) => r.department },
                { header: "Month", get: (r) => r.month || "" },
                { header: "Capacity", get: (r) => r.capacity },
                { header: "Time Tracked", get: (r) => r.timeTracked },
                { header: "Total Utilization %", get: (r) => r.totalUtilization },
                { header: "Billable Hours", get: (r) => r.billableHours },
                { header: "Non-Billable Hours", get: (r) => r.nonBillableHours },
                { header: "Billable Utilization %", get: (r) => r.billableUtilization },
            ],
        },
        render: ({ data, loading, hasFetched }) => <RunnitResourceUtilization data={data} loading={loading} hasFetched={hasFetched} />,
    },

    {
        key: "daily-hours",
        label: "Daily Hours",
        icon: <TodayOutlined fontSize="small" />,
        filters: { dates: 'day', dropdown: { name: "department", label: "Department", options: "departments" } },
        maxRangeDays: 400,
        fetch: ({ dispatch, startDate, endDate, onDone }) => dispatch(getDailyHours(startDate, endDate, onDone)),
        filterRows: (rows, { department }) => (department === "All" ? rows : rows.filter((r) => r.department === department)),
        summaryTitles: ["Total Hours Logged", "Employees Reporting", "Average Hours per Day"],
        summary: (rows, { days }) => {
            const total = rows.reduce((s, r) => s + (r.total || 0), 0);
            const reporting = rows.filter((r) => (r.total || 0) > 0).length;
            const dayCount = days?.length || 0;
            return [
                { title: "Total Hours Logged", value: hours(total), color: "primary" },
                {
                    title: "Employees Reporting",
                    value: `${reporting} of ${rows.length}`,
                    color: "success.main",
                    subtext: rows.length > reporting ? `${rows.length - reporting} logged nothing` : "Everyone logged time",
                },
                {
                    title: "Average Hours per Day",
                    value: hours(dayCount > 0 ? total / dayCount : 0),
                    color: "secondary",
                    subtext: dayCount > 0 ? `Across ${dayCount} day${dayCount === 1 ? "" : "s"}` : undefined,
                },
            ];
        },
        csv: {
            file: ({ stamp }) => `daily_hours_${stamp}.csv`,
            cols: ({ days }) => [
                { header: "Employee", get: (r) => r.employeeName },
                { header: "Department", get: (r) => r.department },
                ...days.map((day) => ({ header: day.key, get: (r) => r.daily?.[day.key] || 0 })),
                { header: "Total", get: (r) => r.total || 0 },
            ],
        },
        render: ({ data, loading, hasFetched, days, startDate, endDate }) => <RunnitDailyHours data={data} loading={loading} hasFetched={hasFetched} days={days} startDate={startDate} endDate={endDate} />,
    },

    {
        key: "hours-posted",
        label: "Hours Posted",
        icon: <AccessTimeOutlined fontSize="small" />,
        filters: { dates: 'month', dropdown: null },
        maxRangeDays: 400,
        fetch: ({ dispatch, startDate, endDate, onDone }) => dispatch(getHoursPosted(startDate, endDate, (data) => onDone(data ? data.rows : null, data))),
        summaryTitles: ["Total Hours Posted", "Clients Posting Time", "Billable Hours"],
        summary: (rows) => {
            const total = rows.reduce((s, r) => s + (r.total || 0), 0);
            const internal = rows.reduce((s, r) => s + (r.byType?.Internal || 0), 0);
            const billable = total - internal;
            return [
                { title: "Total Hours Posted", value: hours(total), color: "primary", subtext: "All billing types" },
                { title: "Clients Posting Time", value: rows.length, color: "secondary" },
                {
                    title: "Billable Hours",
                    value: hours(billable),
                    color: "success.main",
                    subtext: `${hours(internal)} internal and non-billable hours excluded`,
                },
            ];
        },
        csv: {
            file: ({ stamp }) => `hours_posted_${stamp}.csv`,
            rows: (rows) => rows.flatMap((r) => [
                { clientName: r.clientName, month: "All months", byType: r.byType, total: r.total },
                ...(r.months || []).map((m) => ({ clientName: r.clientName, month: monthLabel(m.month), byType: m.byType, total: m.total })),
            ]),
            cols: [
                { header: "Client", get: (r) => r.clientName },
                { header: "Month", get: (r) => r.month },
                ...HOURS_POSTED_TYPES.map((type) => ({ header: type, get: (r) => (r.byType?.[type] || 0).toFixed(2) })),
                { header: "Total Hours", get: (r) => (r.total || 0).toFixed(2) },
            ],
        },
        render: ({ data, loading, hasFetched, failed, startDate, endDate }) => (
            <RunnitHoursPosted data={data} loading={loading} hasFetched={hasFetched} failed={failed} startDate={startDate} endDate={endDate} />
        ),
    },

    {
        key: "ar-aging",
        label: "AR Aging",
        icon: <RequestQuote fontSize="small" />,
        filters: { dates: false, dropdown: null },
        fetch: ({ dispatch, onDone }) => dispatch(getArAging(onDone)),
        summaryTitles: ["Total Outstanding", "90+ Days", "Clients with Open AR"],
        summary: (rows) => {
            const outstanding = rows.reduce((s, r) => s + (r.total || 0), 0);
            const over90 = rows.reduce((s, r) => s + (r.d90plus || 0), 0);
            return [
                { title: "Total Outstanding", value: money(outstanding), color: "primary" },
                { title: "90+ Days", value: money(over90), color: over90 > 0 ? "error.main" : "success.main" },
                { title: "Clients with Open AR", value: rows.length, color: "secondary" },
            ];
        },
        csv: {
            file: () => `ar_aging_${toLocalDay()}.csv`,
            cols: [
                { header: "Client", get: (r) => r.clientName },
                { header: "Open Invoices", get: (r) => r.invoiceCount },
                { header: "Current", get: (r) => (r.current || 0).toFixed(2) },
                { header: "1-30 Days", get: (r) => (r.d1to30 || 0).toFixed(2) },
                { header: "31-60 Days", get: (r) => (r.d31to60 || 0).toFixed(2) },
                { header: "61-90 Days", get: (r) => (r.d61to90 || 0).toFixed(2) },
                { header: "90+ Days", get: (r) => (r.d90plus || 0).toFixed(2) },
                { header: "Total", get: (r) => (r.total || 0).toFixed(2) },
            ],
        },
        render: ({ data, loading, hasFetched }) => <RunnitArAging data={data} loading={loading} hasFetched={hasFetched} />,
    },

    {
        key: "trend",
        label: "Profitability Trend",
        icon: <ShowChart fontSize="small" />,
        filters: { dates: 'month', dropdown: null },
        fetch: ({ dispatch, startDate, endDate, onDone }) => dispatch(getProfitabilityTrend(startDate, endDate, onDone)),
        summaryTitles: ["Total Revenue", "Total Labor Cost", "Average Margin"],
        summary: (rows) => {
            const expected = rows.reduce((s, m) => s + (m.expectedRevenue || 0), 0);
            const actual = rows.reduce((s, m) => s + (m.actualRevenue || 0), 0);
            const cost = rows.reduce((s, m) => s + (m.laborCost || 0), 0);
            const expectedProfit = expected - cost;
            const actualProfit = actual - cost;
            return [
                { title: "Expected Revenue", value: moneyRounded(expected), color: "primary", subtext: "Sum of invoiced amounts" },
                { title: "Actual Revenue", value: moneyRounded(actual), color: "info.main", subtext: "Payments received" },
                { title: "Total Labor Cost", value: moneyRounded(cost), color: "warning.main", subtext: "From employee payment ledger" },
                {
                    title: "Expected Profit",
                    value: moneyRounded(expectedProfit),
                    color: expectedProfit >= 0 ? "success.main" : "error.main",
                    subtext: `${expected > 0 ? ((expectedProfit / expected) * 100).toFixed(1) : "0.0"}% margin`,
                },
                {
                    title: "Actual Profit",
                    value: moneyRounded(actualProfit),
                    color: actualProfit >= 0 ? "success.main" : "error.main",
                    subtext: `${actual > 0 ? ((actualProfit / actual) * 100).toFixed(1) : "0.0"}% margin`,
                },
            ];
        },
        csv: {
            file: ({ stamp }) => `profitability_trend_${stamp}.csv`,
            cols: [
                { header: "Month", get: (r) => r.month },
                { header: "Revenue", get: (r) => (r.revenue || 0).toFixed(2) },
                { header: "Labor Cost", get: (r) => (r.laborCost || 0).toFixed(2) },
                { header: "Profit", get: (r) => (r.profit || 0).toFixed(2) },
                { header: "Margin %", get: (r) => r.margin || 0 },
                { header: "Billable Hours", get: (r) => r.billableHours || 0 },
            ],
        },
        render: ({ data, loading, hasFetched }) => <RunnitProfitabilityTrend data={data} loading={loading} hasFetched={hasFetched} />,
    },

    {
        key: "meeting-time",
        label: "Meeting Time",
        icon: <EventAvailable fontSize="small" />,
        permission: "manageCalendar",
        standalone: true,
        render: ({ startDate, endDate, handlers }) => (
            <MeetingTimeReport startDate={startDate} endDate={endDate} onStartDateChange={handlers.setStartDate} onEndDateChange={handlers.setEndDate} />
        ),
    },
];

export const DEFAULT_REPORT_KEY = "retainer";

export const getReport = (key) => RUNNIT_REPORTS.find((r) => r.key === key) || null;

export const visibleReports = (hasPermission) => RUNNIT_REPORTS.filter((r) => !r.permission || hasPermission(r.permission));
